import React, { useState } from 'react';
import { Autocomplete, Box, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';

import { InlineNotification } from '@paragon/notification-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { addToScheduleLineGroup, createScheduleLineGroup } from '../../../actions/scheduleLineGroupActions';
import {
  getActiveScheduleLineGroupsByLineGuid,
  getActiveScheduleLineGroupsForCheckedBundlesWithSameScheduleGroupByLineGuid,
  getScheduleGridCheckedBundleGuids,
  getScheduleGroupsForCheckedRows,
} from '../../../selectors/scheduleGrid';
import { getStatusesByScheduleLineGroupGuid } from '../../../selectors/scheduleLineGroups';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { LineSelector } from '../../LineSelector';
import { ScheduleLineGroupProperties } from './ScheduleLineGroupProperties';

export function CreateOrAddToScheduleLineGroupDialog() {
  const dispatch = useAppDispatch();
  const checkedBundleGuids = useAppSelector(getScheduleGridCheckedBundleGuids);
  const scheduleGroupsForCheckedRows = useAppSelector(getScheduleGroupsForCheckedRows);
  const statusesByScheduleLineGroupGuid = useAppSelector(getStatusesByScheduleLineGroupGuid);
  const activeScheduleLineGroupsByLineGuid = useAppSelector(getActiveScheduleLineGroupsByLineGuid);
  const scheduleLineGroupsForCheckedBundlesWithSameScheduleGroupByLineGuid = useAppSelector(
    getActiveScheduleLineGroupsForCheckedBundlesWithSameScheduleGroupByLineGuid,
  );

  const [createOrAddTo, setCreateOrAddTo] = useState<'create' | 'add-to'>('create');

  const [newScheduleLineGroupName, setNewScheduleLineGroupName] = useState<string | null>(
    scheduleGroupsForCheckedRows.map((scheduleGroup) => scheduleGroup.name).join(', '),
  );
  const [newScheduleLineGroupNameError, setNewScheduleLineGroupNameError] = useState<boolean>(false);
  const [createActionSelectedLineGuid, setCreateActionSelectedLineGuid] = useState<string | null>(null);
  const [createActionSelectedLineGuidError, setCreateActionSelectedLineGuidError] = useState<boolean>(false);
  const [scheduledDate, setScheduledDate] = useState<string | null>(null);

  const [addToActionSelectedLineGuid, setAddToActionSelectedLineGuid] = useState<string | null>(null);
  const [selectedScheduleLineGroupGuid, setSelectedScheduleLineGroupGuid] = useState<string | null>(null);

  // The user should not be able to create a Schedule Line Group with the same name as another active one.
  const getExistingScheduleLineGroupNamesForLineGuid = (lineGuid: string | null) =>
    lineGuid ? activeScheduleLineGroupsByLineGuid[lineGuid].map((scheduleLineGroup) => scheduleLineGroup.name) : [];
  const existingScheduleLineGroupNamesForLineGuid =
    getExistingScheduleLineGroupNamesForLineGuid(createActionSelectedLineGuid);

  // The user should only be able to add bundles to a Schedule Line Group that has bundles in the same Schedule Group as
  // the checked bundles.
  const addToScheduleLineGroupOptions = addToActionSelectedLineGuid
    ? scheduleLineGroupsForCheckedBundlesWithSameScheduleGroupByLineGuid[addToActionSelectedLineGuid]
    : [];

  const selectedScheduleLineGroupHasBundlesWithStatusOtherThanScheduled =
    selectedScheduleLineGroupGuid === null
      ? false
      : statusesByScheduleLineGroupGuid[selectedScheduleLineGroupGuid].some((status) => status !== 'Scheduled');

  function submit() {
    switch (createOrAddTo) {
      case 'create': {
        if (!createActionSelectedLineGuid) {
          setCreateActionSelectedLineGuidError(true);
        }
        if (!newScheduleLineGroupName || !createActionSelectedLineGuid) return;
        dispatch(
          createScheduleLineGroup(
            checkedBundleGuids,
            newScheduleLineGroupName,
            createActionSelectedLineGuid,
            scheduledDate,
          ),
        );
        break;
      }
      case 'add-to': {
        dispatch(addToScheduleLineGroup(selectedScheduleLineGroupGuid!, checkedBundleGuids));
        break;
      }
      default:
        assertExhaustiveSwitch(createOrAddTo);
    }

    dispatch(closeCurrentPopup());
  }

  let submitText: string;
  switch (createOrAddTo) {
    case 'create': {
      submitText = 'Create';
      break;
    }
    case 'add-to': {
      submitText = 'Add To';
      break;
    }

    default:
      assertExhaustiveSwitch(createOrAddTo);
  }

  let submitDisabled: boolean;
  switch (createOrAddTo) {
    case 'create': {
      submitDisabled =
        newScheduleLineGroupName === null ||
        existingScheduleLineGroupNamesForLineGuid.includes(newScheduleLineGroupName) ||
        createActionSelectedLineGuid === null ||
        createActionSelectedLineGuidError ||
        newScheduleLineGroupNameError;
      break;
    }
    case 'add-to': {
      submitDisabled =
        selectedScheduleLineGroupGuid === null || selectedScheduleLineGroupHasBundlesWithStatusOtherThanScheduled;
      break;
    }
    default:
      assertExhaustiveSwitch(createOrAddTo);
  }

  return (
    <Dialog
      title="Create/Add to Schedule Line Group"
      open
      submitText={submitText}
      onSubmit={submit}
      onCancel={() => void dispatch(closeCurrentPopup())}
      contentSx={{ width: '400px', height: '350px' }}
      submitDisabled={submitDisabled}
      enterKeySubmits
    >
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={createOrAddTo}
          exclusive
          onChange={(event, value) => {
            if (value !== null) setCreateOrAddTo(value);
          }}
        >
          <ToggleButton value="create">Create</ToggleButton>
          <ToggleButton value="add-to">Add To</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {createOrAddTo === 'create' && (
        <>
          <ScheduleLineGroupProperties
            scheduleLineGroupName={newScheduleLineGroupName}
            setScheduleLineGroupName={(updatedNewScheduleLineGroupName) => {
              setNewScheduleLineGroupName(updatedNewScheduleLineGroupName);
              setNewScheduleLineGroupNameError(
                updatedNewScheduleLineGroupName === '' ||
                  (!!updatedNewScheduleLineGroupName &&
                    existingScheduleLineGroupNamesForLineGuid.includes(updatedNewScheduleLineGroupName)),
              );
            }}
            scheduleLineGroupNameError={newScheduleLineGroupNameError}
            selectedLineGuid={createActionSelectedLineGuid}
            setSelectedLineGuid={(lineGuid) => {
              setCreateActionSelectedLineGuid(lineGuid);
              if (lineGuid) setCreateActionSelectedLineGuidError(false);
              setNewScheduleLineGroupNameError(
                !!newScheduleLineGroupName &&
                  getExistingScheduleLineGroupNamesForLineGuid(lineGuid).includes(newScheduleLineGroupName),
              );
            }}
            selectedLineGuidError={createActionSelectedLineGuidError}
            scheduledDate={scheduledDate}
            setScheduledDate={setScheduledDate}
          />
          {newScheduleLineGroupName && existingScheduleLineGroupNamesForLineGuid.includes(newScheduleLineGroupName) && (
            <InlineNotification type="error">
              The name must be different than one that is already on the line.
            </InlineNotification>
          )}
        </>
      )}
      {createOrAddTo === 'add-to' && (
        <>
          <Box sx={{ mt: 1, mb: 2 }}>
            <LineSelector selectedLineGuid={addToActionSelectedLineGuid} submit={setAddToActionSelectedLineGuid} />
          </Box>
          <Autocomplete
            size="small"
            options={addToScheduleLineGroupOptions}
            getOptionLabel={(scheduleLineGroup) => scheduleLineGroup.name}
            renderOption={(props, option) => (
              <li {...props} key={option.guid}>
                {option.name}
              </li>
            )}
            renderInput={(params) => <TextField {...params} variant="outlined" label="Select Schedule Line Group" />}
            value={
              addToActionSelectedLineGuid && selectedScheduleLineGroupGuid
                ? (addToScheduleLineGroupOptions.find(
                    (scheduleLineGroup) => scheduleLineGroup.guid === selectedScheduleLineGroupGuid,
                  ) ?? null)
                : null
            }
            onChange={(event, productionGroup) => void setSelectedScheduleLineGroupGuid(productionGroup?.guid ?? null)}
            disabled={addToActionSelectedLineGuid === null}
          />
          {selectedScheduleLineGroupHasBundlesWithStatusOtherThanScheduled ? (
            <InlineNotification type="error">
              Bundles can only be added to schedule groups where all bundles have the status of &quot;Scheduled&quot;
              (none can be &quot;Started Assembly&quot; or beyond).
            </InlineNotification>
          ) : null}
        </>
      )}
    </Dialog>
  );
}
